import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
    Receipt,
    Banknote,
    Calculator,
    Plus,
    Eye,
    CheckCircle,
    XCircle,
    Clock,
    DollarSign,
    Loader2,
    AlertCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { payoutService } from '@/services/payoutService'
import { PaymentPayoutBatch, MerchantSettlement, PayoutStats } from '@/types'
import { safeFormatDistanceToNow } from '@/utils/date'

export default function AdminPayoutManagement() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const navigate = useNavigate()
    const [stats, setStats] = useState<PayoutStats | null>(null)
    const [recentBatches, setRecentBatches] = useState<PaymentPayoutBatch[]>([])
    const [recentSettlements, setRecentSettlements] = useState<MerchantSettlement[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        fetchPayoutData()
    }, [])

    const fetchPayoutData = async () => {
        try {
            setLoading(true)
            setError(null)

            const [statsResponse, batchesResponse, settlementsResponse] = await Promise.all([
                payoutService.getPayoutStats(),
                payoutService.getPayoutBatches({ page: 1, page_size: 5 }),
                payoutService.getSettlements({ page: 1, page_size: 5 })
            ])

            if (statsResponse.success) {
                setStats(statsResponse.data)
            }

            if (batchesResponse.success) {
                setRecentBatches(batchesResponse.data.results)
            }

            if (settlementsResponse.success) {
                setRecentSettlements(settlementsResponse.data.results)
            }
        } catch (err) {
            setError('An error occurred while fetching payout data')
            console.error('Error fetching payout data:', err)
        } finally {
            setLoading(false)
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'draft':
                return 'text-gray-600 bg-gray-100 border-gray-200'
            case 'calculated':
                return 'text-blue-600 bg-blue-100 border-blue-200'
            case 'approved':
                return 'text-green-600 bg-green-100 border-green-200'
            case 'paid':
                return 'text-green-700 bg-green-200 border-green-300'
            case 'cancelled':
                return 'text-red-600 bg-red-100 border-red-200'
            case 'initiated':
                return 'text-blue-600 bg-blue-100 border-blue-200'
            case 'pending':
                return 'text-yellow-600 bg-yellow-100 border-yellow-200'
            case 'completed':
                return 'text-green-600 bg-green-100 border-green-200'
            case 'failed':
                return 'text-red-600 bg-red-100 border-red-200'
            default:
                return 'text-gray-600 bg-gray-100 border-gray-200'
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'draft':
                return Clock
            case 'calculated':
                return Calculator
            case 'approved':
                return CheckCircle
            case 'paid':
                return CheckCircle
            case 'cancelled':
                return XCircle
            case 'initiated':
                return Clock
            case 'pending':
                return Clock
            case 'completed':
                return CheckCircle
            case 'failed':
                return XCircle
            default:
                return Clock
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: branding.primaryColor }} />
                    <p style={pageStyles.textSecondary}>Loading payout data...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <AlertCircle className="h-8 w-8 mx-auto mb-4" style={{ color: statusColors.error.color }} />
                    <p className="mb-4" style={{ color: statusColors.error.color }}>{error}</p>
                    <Button onClick={fetchPayoutData} variant="outline" style={{
                        color: branding.textSecondaryColor,
                        borderColor: `${branding.textSecondaryColor}40`,
                        backgroundColor: 'transparent'
                    }} className="hover:bg-gray-800 hover:text-white">
                        Try Again
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen" style={pageStyles.container}>
            <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <Button
                                onClick={() => navigate('/admin/payout-batches')}
                                variant="outline"
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                                className="flex items-center hover:bg-gray-800 hover:text-white"
                            >
                                <Receipt className="h-4 w-4 mr-2" />
                                View All Batches
                            </Button>
                            <Button
                                onClick={() => navigate('/admin/settlements')}
                                variant="outline"
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                                className="flex items-center hover:bg-gray-800 hover:text-white"
                            >
                                <Banknote className="h-4 w-4 mr-2" />
                                View All Settlements
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <div className="border backdrop-blur-md shadow-sm rounded-lg p-6" style={cardStyles}>
                            <div className="flex items-center">
                                <div className="p-2 rounded-lg" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                    <Receipt className="h-6 w-6" style={{ color: branding.primaryColor }} />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium" style={pageStyles.textSecondary}>Total Batches</p>
                                    <p className="text-2xl font-bold" style={pageStyles.text}>{stats.total_batches}</p>
                                </div>
                            </div>
                        </div>

                        <div className="border backdrop-blur-md shadow-sm rounded-lg p-6" style={cardStyles}>
                            <div className="flex items-center">
                                <div className="p-2 rounded-lg" style={{ backgroundColor: `${branding.secondaryColor}20` }}>
                                    <Banknote className="h-6 w-6" style={{ color: branding.secondaryColor }} />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium" style={pageStyles.textSecondary}>Total Settlements</p>
                                    <p className="text-2xl font-bold" style={pageStyles.text}>{stats.total_settlements}</p>
                                </div>
                            </div>
                        </div>

                        <div className="border backdrop-blur-md shadow-sm rounded-lg p-6" style={cardStyles}>
                            <div className="flex items-center">
                                <div className="p-2 rounded-lg" style={{ backgroundColor: `${statusColors.success.backgroundColor}` }}>
                                    <DollarSign className="h-6 w-6" style={{ color: statusColors.success.color }} />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium" style={pageStyles.textSecondary}>Total Commission</p>
                                    <p className="text-2xl font-bold" style={pageStyles.text}>${stats.total_commission_amount ? Number(stats.total_commission_amount).toFixed(2) : '0.00'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="border backdrop-blur-md shadow-sm rounded-lg p-6" style={cardStyles}>
                            <div className="flex items-center">
                                <div className="p-2 rounded-lg" style={{ backgroundColor: `${statusColors.info.backgroundColor}` }}>
                                    <Calculator className="h-6 w-6" style={{ color: statusColors.info.color }} />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium" style={pageStyles.textSecondary}>Avg Transaction Amount</p>
                                    <p className="text-2xl font-bold" style={pageStyles.text}>${stats.average_transaction_amount ? Number(stats.average_transaction_amount).toFixed(2) : '0.00'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Recent Payout Batches */}
                    <div className="border backdrop-blur-md shadow-sm rounded-lg" style={cardStyles}>
                        <div className="p-6 border-b" style={{ borderColor: `${branding.textSecondaryColor}30` }}>
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold" style={pageStyles.text}>Recent Payout Batches</h2>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate('/admin/payout-batches')}
                                    style={{ color: branding.primaryColor }}
                                    className="hover:text-orange-300"
                                >
                                    View All
                                </Button>
                            </div>
                        </div>
                        <div className="p-6">
                            {recentBatches.length === 0 ? (
                                <div className="text-center py-8">
                                    <Receipt className="h-12 w-12 mx-auto mb-4" style={{ color: branding.textSecondaryColor }} />
                                    <p style={pageStyles.textSecondary}>No payout batches found</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {recentBatches.map((batch) => {
                                        const StatusIcon = getStatusIcon(batch.status)
                                        return (
                                            <div key={batch.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-white/5" style={{
                                                borderColor: `${branding.textSecondaryColor}30`,
                                                backgroundColor: `${branding.textSecondaryColor}05`
                                            }}>
                                                <div className="flex items-center space-x-4">
                                                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                                        <StatusIcon className="h-5 w-5" style={{ color: branding.primaryColor }} />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium" style={pageStyles.text}>{batch.merchant_name}</p>
                                                        <p className="text-sm" style={pageStyles.textSecondary}>
                                                            {batch.total_transactions} transactions • ${Number(batch.net_amount_fiat).toFixed(2)} {batch.currency}
                                                        </p>
                                                        <p className="text-xs" style={{ color: `${branding.textSecondaryColor}80` }}>
                                                            {safeFormatDistanceToNow(batch.created_at, 'Unknown', { addSuffix: true })}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(batch.status)}`}>
                                                        {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/admin/payout-batches/${batch.id}`)}
                                                        style={{ color: branding.textSecondaryColor }}
                                                        className="hover:text-white"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent Settlements */}
                    <div className="border backdrop-blur-md shadow-sm rounded-lg" style={cardStyles}>
                        <div className="p-6 border-b" style={{ borderColor: `${branding.textSecondaryColor}30` }}>
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold" style={pageStyles.text}>Recent Settlements</h2>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate('/admin/settlements')}
                                    style={{ color: branding.primaryColor }}
                                    className="hover:text-orange-300"
                                >
                                    View All
                                </Button>
                            </div>
                        </div>
                        <div className="p-6">
                            {recentSettlements.length === 0 ? (
                                <div className="text-center py-8">
                                    <Banknote className="h-12 w-12 mx-auto mb-4" style={{ color: branding.textSecondaryColor }} />
                                    <p style={pageStyles.textSecondary}>No settlements found</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {recentSettlements.map((settlement) => {
                                        const StatusIcon = getStatusIcon(settlement.status)
                                        return (
                                            <div key={settlement.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-white/5" style={{
                                                borderColor: `${branding.textSecondaryColor}30`,
                                                backgroundColor: `${branding.textSecondaryColor}05`
                                            }}>
                                                <div className="flex items-center space-x-4">
                                                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${branding.secondaryColor}20` }}>
                                                        <StatusIcon className="h-5 w-5" style={{ color: branding.secondaryColor }} />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium" style={pageStyles.text}>{settlement.merchant_name}</p>
                                                        <p className="text-sm" style={pageStyles.textSecondary}>
                                                            ${Number(settlement.amount_fiat).toFixed(2)} {settlement.currency} • {settlement.method.replace('_', ' ')}
                                                        </p>
                                                        <p className="text-xs" style={{ color: `${branding.textSecondaryColor}80` }}>
                                                            {safeFormatDistanceToNow(settlement.created_at, 'Unknown', { addSuffix: true })}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(settlement.status)}`}>
                                                        {settlement.status.charAt(0).toUpperCase() + settlement.status.slice(1)}
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/admin/settlements/${settlement.id}`)}
                                                        style={{ color: branding.textSecondaryColor }}
                                                        className="hover:text-white"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-8 border backdrop-blur-md shadow-sm rounded-lg p-6" style={cardStyles}>
                    <h2 className="text-lg font-semibold mb-4" style={pageStyles.text}>Quick Actions</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Button
                            onClick={() => navigate('/admin/payout-batches/create')}
                            className="flex items-center justify-center p-4 h-auto text-white"
                            style={pageStyles.primaryBg}
                        >
                            <Plus className="h-5 w-5 mr-2" />
                            Create Payout Batch
                        </Button>
                        <Button
                            onClick={() => navigate('/admin/settlements/create')}
                            variant="outline"
                            className="flex items-center justify-center p-4 h-auto"
                            style={{
                                color: branding.textSecondaryColor,
                                borderColor: `${branding.textSecondaryColor}40`,
                                backgroundColor: 'transparent'
                            }}
                        >
                            <Banknote className="h-5 w-5 mr-2" />
                            Create Settlement
                        </Button>
                        <Button
                            onClick={() => navigate('/admin/commission-records')}
                            variant="outline"
                            className="flex items-center justify-center p-4 h-auto"
                            style={{
                                color: branding.textSecondaryColor,
                                borderColor: `${branding.textSecondaryColor}40`,
                                backgroundColor: 'transparent'
                            }}
                        >
                            <Calculator className="h-5 w-5 mr-2" />
                            View Commission Records
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
